/**
 * «Выписки → Загрузка»: перетащил пачку файлов — они уходят по одному, и по
 * каждому виден итог. ИП и банк не выбираются: сервер узнаёт их по ИНН и БИК
 * в файле. Не узнал — выписка всё равно принята, и в списке ниже ИП и банк
 * назначаются руками.
 *
 * Спека: `docs/specs/2026-09-29-bank-statements-design.md`.
 */
import { Download, RefreshCw, Trash2, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { Combobox } from "@/components/Combobox";
import { NoAccess } from "@/components/NoAccess";
import { ApiFailure } from "@/lib/api";
import { STATEMENTS, useCan } from "@/lib/access";
import { fileSize, mskTime } from "@/lib/dates";
import { parseAllStatements } from "@/lib/pnl";
import {
  ACCEPT,
  assignStatement,
  BANKS,
  type DirectoryRow,
  ENTREPRENEURS,
  loadDirectory,
  loadStatements,
  recognizeStatements,
  removeStatement,
  type Statement,
  statementFileUrl,
  uploadStatement,
} from "@/lib/statements";


type Queued = {
  key: string;
  file: File;
  state: "wait" | "busy" | "ok" | "partial" | "dup" | "fail";
  note: string;
};

const STATE_TEXT: Record<Queued["state"], string> = {
  wait: "в очереди",
  busy: "загружается…",
  ok: "загружено",
  partial: "загружено, не распознано",
  dup: "уже загружен",
  fail: "ошибка",
};

const STATE_CLASS: Record<Queued["state"], string> = {
  wait: "text-fg-faint",
  busy: "text-fg-muted",
  ok: "text-ok",
  partial: "text-warn",
  dup: "text-fg-muted",
  fail: "text-err",
};

export function StatementsPage() {
  return useCan(STATEMENTS) ? <StatementsScreen /> : <NoAccess what="выпискам" />;
}

function outcome(row: Statement): Pick<Queued, "state" | "note"> {
  const who = [row.entrepreneur_name, row.bank_name].filter(Boolean).join(", ");
  if (row.entrepreneur_id !== null && row.bank_id !== null) return { state: "ok", note: who };
  return { state: "partial", note: who ? `узнали только: ${who}` : "ИП и банк назначьте в списке ниже" };
}

function StatementsScreen() {
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [banks, setBanks] = useState<DirectoryRow[]>([]);
  const [rows, setRows] = useState<Statement[] | null>(null);
  const [queue, setQueue] = useState<Queued[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  const reloadRows = useCallback(() => {
    loadStatements()
      .then(setRows)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  useEffect(() => {
    loadDirectory(ENTREPRENEURS)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
    loadDirectory(BANKS)
      .then(setBanks)
      .catch((error: Error) => setFailure(error.message));
    reloadRows();
  }, [reloadRows]);

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setFailure("");
    setNotice("");
    const added = Array.from(list).map((file, i) => ({
      key: `${Date.now()}-${i}-${file.name}`,
      file,
      state: "wait" as const,
      note: "",
    }));
    // Уже обработанные из прошлой пачки убираем — на экране только новая.
    setQueue((prev) => [...prev.filter((q) => q.state === "wait"), ...added]);
    if (fileInput.current) fileInput.current.value = "";
  };

  const patch = (key: string, change: Partial<Queued>) =>
    setQueue((prev) => prev.map((q) => (q.key === key ? { ...q, ...change } : q)));

  /** По одному: пачка из сотни файлов не должна разом занять сервис, а
   *  отказ по одному файлу (дубль, не тот формат) не останавливает остальные. */
  const uploadAll = async () => {
    setBusy(true);
    setFailure("");
    setNotice("");
    for (const item of queue.filter((q) => q.state === "wait")) {
      patch(item.key, { state: "busy" });
      try {
        patch(item.key, outcome(await uploadStatement(item.file)));
      } catch (error) {
        const code = error instanceof ApiFailure ? error.items[0]?.code : undefined;
        patch(item.key, {
          state: code === "DUPLICATE_FILE" ? "dup" : "fail",
          note: (error as Error).message,
        });
      }
    }
    setBusy(false);
    reloadRows();
  };

  const assign = (row: Statement, change: { entrepreneur_id?: number; bank_id?: number }) => {
    setFailure("");
    assignStatement(row.id, change)
      .then((fresh) => setRows((prev) => (prev ?? []).map((r) => (r.id === fresh.id ? fresh : r))))
      .catch((error: Error) => setFailure(error.message));
  };

  const recognizeAgain = () => {
    setFailure("");
    setNotice("");
    recognizeStatements()
      .then((r) => {
        setNotice(`Проверено ${r.checked}, распознано ${r.recognized}`);
        reloadRows();
      })
      .catch((error: Error) => setFailure(error.message));
  };

  const parseAgain = () => {
    setFailure("");
    setNotice("");
    parseAllStatements()
      .then((r) => {
        setNotice(`Проверено ${r.checked}, разобрано ${r.parsed}`);
        reloadRows();
      })
      .catch((error: Error) => setFailure(error.message));
  };

  const remove = (row: Statement) => {
    if (!window.confirm(`Убрать из списка «${row.file_name}»? Файл в хранилище останется.`)) return;
    setFailure("");
    removeStatement(row.id)
      .then(reloadRows)
      .catch((error: Error) => setFailure(error.message));
  };

  const waiting = queue.filter((q) => q.state === "wait").length;
  const unassigned = (rows ?? []).filter((r) => r.entrepreneur_id === null || r.bank_id === null).length;
  const unparsed = (rows ?? []).filter((r) => r.status_code !== "parsed").length;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 [&>*]:flex-none">
      <section className="grid max-w-[760px] gap-3 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInput.current?.click()}
          className={`grid cursor-pointer place-items-center rounded-[var(--radius-ui)] border border-dashed px-4 py-6 text-center text-[12.5px] ${
            dragOver ? "border-accent bg-accent-dim" : "border-border-strong bg-bg hover:bg-bg-hover"
          }`}
        >
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          <div className="text-fg-muted">
            Перетащите сюда выписки — сколько угодно файлов сразу — или нажмите, чтобы выбрать
            <div className="mt-1 text-[11.5px] text-fg-faint">
              Excel (.xlsx, .xls), .csv, выгрузка 1С (.txt), .pdf — до 20 МБ каждый. ИП и банк узнаются по ИНН и
              БИК из{" "}
              <Link to="/statements/entrepreneurs" className="text-accent-text hover:underline" onClick={(e) => e.stopPropagation()}>
                справочника ИП
              </Link>{" "}
              и{" "}
              <Link to="/statements/banks" className="text-accent-text hover:underline" onClick={(e) => e.stopPropagation()}>
                банков
              </Link>
              .
            </div>
          </div>
        </div>

        {queue.length > 0 ? (
          <div className="max-h-[280px] overflow-y-auto rounded-[var(--radius-ui)] border border-border bg-bg">
            {queue.map((q) => (
              <div key={q.key} className="flex items-center gap-3 border-b border-border px-3 py-1.5 text-[12px] last:border-b-0">
                <span className="min-w-0 flex-1 truncate" title={q.file.name}>
                  {q.file.name}
                </span>
                <span className="whitespace-nowrap tabular-nums text-fg-faint">{fileSize(q.file.size)}</span>
                <span className={`whitespace-nowrap ${STATE_CLASS[q.state]}`} title={q.note}>
                  {STATE_TEXT[q.state]}
                  {q.note ? ` · ${q.note}` : ""}
                </span>
                {q.state === "wait" && !busy ? (
                  <button
                    type="button"
                    title="Убрать из очереди"
                    onClick={() => setQueue((prev) => prev.filter((x) => x.key !== q.key))}
                    className="rounded p-0.5 text-fg-faint hover:text-err"
                  >
                    <X size={12} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={waiting === 0 || busy}
            onClick={() => void uploadAll()}
            className="flex h-9 items-center gap-2 rounded-[var(--radius-ui)] bg-fg px-4 text-[13px] font-medium text-bg disabled:opacity-50"
          >
            <Upload size={14} />
            {busy ? "Загружаем…" : waiting > 0 ? `Загрузить все (${waiting})` : "Загрузить"}
          </button>
          {queue.length > 0 && !busy ? (
            <button type="button" onClick={() => setQueue([])} className="text-[12px] text-fg-muted hover:underline">
              очистить
            </button>
          ) : null}
        </div>

        {failure ? (
          <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
            {failure}
          </div>
        ) : null}
      </section>

      <div className="flex items-center gap-3">
        <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-fg-faint">Загруженные выписки</div>
        {unassigned > 0 ? (
          <>
            <span className="text-[12px] text-warn">не распознано: {unassigned}</span>
            <button
              type="button"
              onClick={recognizeAgain}
              title="После пополнения справочников ИП и банков"
              className="flex items-center gap-1 text-[12px] text-accent-text hover:underline"
            >
              <RefreshCw size={12} />
              распознать заново
            </button>
          </>
        ) : null}
        {unparsed > 0 ? (
          <>
            <span className="text-[12px] text-warn">не разобрано в операции: {unparsed}</span>
            <button
              type="button"
              onClick={parseAgain}
              title="После пополнения справочника ИП"
              className="flex items-center gap-1 text-[12px] text-accent-text hover:underline"
            >
              <RefreshCw size={12} />
              разобрать заново
            </button>
          </>
        ) : null}
        {notice ? <span className="text-[12px] text-ok">{notice}</span> : null}
      </div>
      {rows === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">Пока ничего не загружено.</div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className="border-b border-border px-3 py-2 text-left">загружена</th>
                <th className="border-b border-border px-3 py-2 text-left">ИП</th>
                <th className="border-b border-border px-3 py-2 text-left">банк</th>
                <th className="border-b border-border px-3 py-2 text-left">счёт</th>
                <th className="border-b border-border px-3 py-2 text-left">период</th>
                <th className="border-b border-border px-3 py-2 text-right">операций</th>
                <th className="border-b border-border px-3 py-2 text-left">файл</th>
                <th className="border-b border-border px-3 py-2 text-right">размер</th>
                <th className="border-b border-border px-3 py-2 text-left">кто</th>
                <th className="border-b border-border px-3 py-2 text-left">статус</th>
                <th className="border-b border-border px-3 py-2 text-left">как распознан</th>
                <th className="w-[1%] border-b border-border px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-annot={`выписка ${row.id}`} className="hover:bg-bg-hover">
                  <td className="whitespace-nowrap border-b border-border px-3 py-1.5 tabular-nums text-fg-muted">
                    {mskTime(row.uploaded_at)}
                  </td>
                  <td className="border-b border-border px-3 py-1.5">
                    {row.entrepreneur_name ?? (
                      <Pick
                        options={entrepreneurs}
                        empty="выберите ИП"
                        hint={row.found_inn ? `в файле ИНН ${row.found_inn}` : "ИНН в файле не найден"}
                        onPick={(id) => assign(row, { entrepreneur_id: id })}
                      />
                    )}
                  </td>
                  <td className="border-b border-border px-3 py-1.5">
                    {row.bank_name ?? (
                      <Pick
                        options={banks}
                        empty="выберите банк"
                        hint={
                          row.account_bank_name
                            ? `по счёту: ${row.account_bank_name}`
                            : row.found_bik
                              ? `в файле БИК ${row.found_bik}`
                              : "БИК в файле не найден"
                        }
                        onPick={(id) => assign(row, { bank_id: id })}
                      />
                    )}
                  </td>
                  <td className="whitespace-nowrap border-b border-border px-3 py-1.5 tabular-nums text-fg-muted">
                    {row.account ?? "—"}
                    {row.account_kind_name ? (
                      <div className="text-[10.5px] text-fg-faint">{row.account_kind_name}</div>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap border-b border-border px-3 py-1.5 tabular-nums text-fg-muted">
                    {row.date_from ? `${day(row.date_from)} — ${day(row.date_to)}` : "—"}
                  </td>
                  <td className="border-b border-border px-3 py-1.5 text-right tabular-nums text-fg-muted">
                    {row.operation_count ?? "—"}
                  </td>
                  <td className="border-b border-border px-3 py-1.5">
                    <a href={statementFileUrl(row.id)} className="inline-flex items-center gap-1.5 hover:underline">
                      <Download size={12} className="flex-none text-fg-faint" />
                      {row.file_name}
                    </a>
                    <div className="text-[10.5px] text-fg-faint">{row.kind_name ?? "тип ещё не определён"}</div>
                  </td>
                  <td className="whitespace-nowrap border-b border-border px-3 py-1.5 text-right tabular-nums text-fg-muted">
                    {fileSize(row.file_size)}
                  </td>
                  <td className="border-b border-border px-3 py-1.5 text-fg-muted">{row.user_name ?? row.user_id}</td>
                  <td className="max-w-[280px] border-b border-border px-3 py-1.5">
                    <span className={row.status_code === "parsed" ? "text-ok" : row.status_code === "failed" ? "text-err" : "text-fg-muted"}>
                      {row.status_name}
                    </span>
                    {row.parse_error ? <div className="text-[11px] text-fg-faint">{row.parse_error}</div> : null}
                  </td>
                  <td className="min-w-[320px] max-w-[520px] border-b border-border px-3 py-1.5 text-[11px] leading-snug text-fg-muted">
                    {row.recognition ?? "—"}
                  </td>
                  <td className="border-b border-border px-3 py-1.5 text-right">
                    <button
                      type="button"
                      title="Убрать из списка"
                      onClick={() => remove(row)}
                      className="rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-err"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

function day(iso: string | null): string {
  return iso ? iso.split("-").reverse().join(".") : "—";
}

/** Выбор ИП или банка у не распознанной выписки; под ним — что нашлось в файле. */
function Pick(props: { options: DirectoryRow[]; empty: string; hint: string; onPick: (id: number) => void }) {
  return (
    <div className="grid gap-0.5">
      <Combobox
        size="sm"
        className="w-full"
        value=""
        placeholder={props.options.length ? props.empty : "справочник пуст"}
        onChange={(v) => props.onPick(Number(v))}
        options={props.options.map((o) => ({ value: String(o.id), label: o.code ? `${o.name} · ${o.code}` : o.name }))}
      />
      <span className="text-[10.5px] text-fg-faint">{props.hint}</span>
    </div>
  );
}
